<template>
  <section class="fixed inset-0 flex items-center justify-center z-[29] bg-black bg-opacity-30 backdrop-blur-sm mt-[70px]">
    <div id="modal modal-table"
      class="modal relative block bg-white dark:bg-dark-800 w-5/6 max-w-4xl shadow-md rounded-lg dark:shadow-dark-600 transform transition-all duration-300 ease-in-out"
      :class="{
        'animate__animated animate__zoomIn': showModal,
        'animate__animated animate__zoomOut': !showModal,
        'scale-95 opacity-90': !showModal
      }">
      <div id="modal-header" class="w-full bg-primary dark:bg-dark-600 py-3 rounded-t-lg">
        <h5 class="text-white text-center font-bold text-xl">Import Tiktok</h5>
      </div>
      <div id="modal-body" class="p-5 space-y-4 max-h-[calc(100dvh-220px)] overflow-y-auto">
        <InputTextCustom id="import-marketplace" label="File" v-model="file" type="file" @change="handleFileUpload" />

        <!-- Import Progress Tracking -->
        <div v-if="importProgress.status === 'processing'" class="mt-4 p-4 bg-blue-100 rounded-lg">
          <div class="flex justify-between items-center mb-2">
            <span class="font-semibold">Importing Items</span>
            <span>{{ importProgress.current }} / {{ importProgress.total }}</span>
          </div>
          <progress :value="importProgress.current" :max="importProgress.total"
            class="w-full h-2 bg-blue-200 rounded-full"></progress>
          <div v-if="importProgress.currentItem" class="mt-2 text-sm">
            <p>Current Item: {{ importProgress.currentItem.order_id }}</p>
            <p>Product: {{ importProgress.currentItem.items[0].product_name }}</p>
          </div>
        </div>

        <!-- Completed State -->
        <div v-else-if="importProgress.status === 'completed'" class="mt-4 p-4 bg-green-100 rounded-lg">
          <p class="text-green-700 font-semibold">Import Completed Successfully!</p>
          <p>Total Items: {{ importProgress.total }}</p>
        </div>

        <!-- Error State -->
        <div v-else-if="importProgress.status === 'error'" class="mt-4 p-4 bg-red-100 rounded-lg">
          <p class="text-red-700 font-semibold">Import Failed</p>
          <p>An error occurred during the import process</p>
        </div>

        <!-- Success Items Section -->
        <div v-if="importProgress.success && importProgress.success.length > 0" class="mt-4 p-4 bg-green-50 rounded-lg">
          <div class="flex justify-between items-center mb-2">
            <p class="text-green-700 font-semibold">
              Successfully Imported Items
            </p>
            <button @click="toggleSuccessSection = !toggleSuccessSection"
              class="text-green-600 hover:text-green-800 focus:outline-none">
              <i class="pi pi-chevron-up" v-if="toggleSuccessSection"></i>
              <i class="pi pi-chevron-down" v-else></i>
            </button>
          </div>
          <div v-if="toggleSuccessSection" class="max-h-40 overflow-y-auto">
            <table class="w-full text-sm">
              <thead>
                <tr class="bg-green-200">
                  <th class="p-2 text-left">Order ID</th>
                  <th class="p-2 text-left">Products</th>
                  <th class="p-2 text-left">Total Amount</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(result, index) in importProgress.success" :key="index"
                  class="border-b border-green-200 hover:bg-green-100">
                  <td class="p-2">
                    {{
                      (() => {
                        console.log('Success result:', result);
                        if (result === null || result === undefined) return 'N/A';
                        if (result.item && result.item.order_id) return result.item.order_id;
                        if (result.order_id) return result.order_id;
                        return 'N/A';
                      })()
                    }}
                  </td>
                  <td class="p-2">
                    {{
                      (() => {
                        if (result === null || result === undefined) return 'N/A';
                        if (result.item && result.item.items)
                          return result.item.items.map(product => product.product_name).filter(Boolean).join(', ');
                        if (result.items)
                          return result.items.map(product => product.product_name).filter(Boolean).join(', ');
                        return 'N/A';
                      })()
                    }}
                  </td>
                  <td class="p-2">
                    {{
                      (() => {
                        if (result === null || result === undefined) return 0;
                        if (result.item && result.item.grand_total) return result.item.grand_total;
                        if (result.grand_total) return result.grand_total;
                        return 0;
                      })()
                    }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <div v-else-if="importProgress.success.length === 0" class="text-center text-gray-500">
            No items successfully imported
          </div>
          <p class="mt-2 text-sm text-green-600">
            Total Successful Items: {{ importProgress.success.length }} /
            {{ importedData?.length || 0 }} items imported
          </p>
        </div>

        <!-- Partial Error State -->
        <div v-if="importProgress.errors && importProgress.errors.length > 0" class="mt-4 p-4 bg-yellow-100 rounded-lg">
          <div class="flex justify-between items-center mb-2">
            <p class="text-yellow-700 font-semibold">Import Errors</p>
            <button @click="toggleErrorSection = !toggleErrorSection"
              class="text-warning-600 hover:text-warning-800 focus:outline-none">
              <i class="pi pi-chevron-up" v-if="toggleErrorSection"></i>
              <i class="pi pi-chevron-down" v-else></i>
            </button>
          </div>
          <div v-if="importProgress.errors.length > 0">
            <p>Failed to import {{ importProgress.errors.length }} items:</p>
            <div v-if="toggleErrorSection" class="max-h-40 overflow-y-auto">
              <table class="w-full text-sm">
                <thead>
                  <tr class="bg-yellow-200">
                    <th class="p-2 text-left">Order ID</th>
                    <th class="p-2 text-left">Product</th>
                    <th class="p-2 text-left">Total Amount</th>
                    <th class="p-2 text-left">Error Message</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(error, index) in importProgress.errors" :key="index"
                    class="border-b border-yellow-200 hover:bg-yellow-50">
                    <td class="p-2">{{ error.item?.order_id || 'N/A' }}</td>
                    <td class="p-2">
                      {{
                        error.item?.items?.map(item => item.product_name).filter(Boolean).join(', ') || 'N/A'
                      }}
                    </td>
                    <td class="p-2">{{ error.item?.grand_total || 0 }}</td>
                    <td class="p-2 text-red-600">{{ error.errorMessage || 'Unknown error' }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div class="mt-2 flex justify-between items-center">
              <p class="text-sm text-yellow-800">
                {{ importProgress.errors.length }} /
                {{ importedData?.length || 0 }} items imported
              </p>
              <button class="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded"
                @click="exportFailedItems">
                Export Failed Items
              </button>
            </div>
          </div>
          <div v-else class="text-center text-gray-500">
            No import errors
          </div>
        </div>
      </div>
      <div class="modal-footer p-5">
        <div class="flex justify-center">
          <SubmitButton id="close" button_name="Close"
            button_class="mx-1 border border-primary bg-white dark:bg-dark-800 dark:border-dark-100 hover:bg-hover-danger hover:border-white hover:text-white text-primary dark:text-white"
            @click="closeModal" />
          <SubmitButton id="submit" button_name="Submit"
            button_class="mx-1 bg-primary hover:bg-hover-primary text-white" @click="submitImportData"
            :disabled="importProgress.status === 'processing'" />
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import SubmitButton from '../form/SubmitButton.vue';
import InputTextCustom from '../form/InputTextCustom.vue';
import * as XLSX from 'xlsx';
import { mapActions } from 'vuex';

export default {
  name: "ModalImportTiktok",
  props: {
    product: {
      type: Object,
      default: null,
    }
  },
  components: {
    SubmitButton, InputTextCustom
  },
  data() {
    return {
      showModal: true,
      file: null,
      importedData: null,
      isEdit: !!this.product,
      importProgress: {
        current: 0,
        total: 0,
        status: 'idle', // 'idle', 'processing', 'completed', 'error'
        currentItem: null,
        errors: [], // Array to store individual item errors
        failedItemsFile: null, // To store the exported failed items file
        success: [],
      },
      toggleSuccessSection: false,
      toggleErrorSection: false,
    }
  },
  methods: {
    ...mapActions('sales_orders', ['importSalesOrder']),
    handleFileUpload(event) {
      const file = event.target.files[0];
      if (file) {
        const reader = new FileReader();
        reader.onload = (e) => {
          const workbook = XLSX.read(e.target.result, { type: 'binary' });
          const sheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[sheetName];
          const jsonData = XLSX.utils.sheet_to_json(worksheet);
          console.log('jsonData', jsonData);

          // Group by invoice number
          const groupedData = jsonData.reduce((acc, row) => {
            const invoiceNo = row['Order/adjustment ID '] || this.generateOrderId();

            // If invoice number already exists, add item to existing entry
            if (acc[invoiceNo]) {
              acc[invoiceNo].items.push({
                product_name: row['Product Name'] || '',
                qty: 1, // Assuming single item, adjust if needed
                unit_price: row['Total Product Amount'] || 0,
                discount: row['Promo'] || 0,
                product_id: row['Product ID'] || '',
                service_fee_gross: row['Service Fee Gross'] || 0,
                service_fee_net: row['Service Fee Net'] || 0,
                ppn: row['PPN'] || 0,
                pph: row['PPH'] || 0,
              });

              // Update totals
              acc[invoiceNo].sub_total += row['Total Product Amount'] || 0;
              acc[invoiceNo].grand_total += (row['Total Product Amount'] || 0) + (row['Service Fee Net'] || 0);
              acc[invoiceNo].discount += row['Promo'] || 0;
            } else {
              // Create new entry
              acc[invoiceNo] = {
                createdAt: this.formatDate(row['Finish Date']),
                updatedAt: this.formatDate(row['Finish Date']),
                delivery_date: this.formatDate(row['Finish Date']),
                delivery_fee: row['Service Fee Net'] || 0,
                order_id: invoiceNo,
                sub_total: row['Total Product Amount'] || 0,
                grand_total: (row['Total Product Amount'] || 0) + (row['Service Fee Net'] || 0),
                discount: row['Promo'] || 0,
                note_order: row['Commission Name'] || '',
                customer_name: '', // No customer name in this data structure
                customer_phone: '', // No customer phone in this data structure
                customer_address: '', // No customer address in this data structure
                due_date: this.formatDate(row['Finish Date']),
                items: [{
                  product_name: row['Product Name'] || '',
                  qty: 1, // Assuming single item, adjust if needed
                  unit_price: row['Total Product Amount'] || 0,
                  discount: row['Promo'] || 0,
                  product_id: row['Product ID'] || '',
                  service_fee_gross: row['Service Fee Gross'] || 0,
                  service_fee_net: row['Service Fee Net'] || 0,
                  ppn: row['PPN'] || 0,
                  pph: row['PPH'] || 0,
                }],
                additional_info: {
                  service_fee_rate: row['Service Fee Rate'] || 0,
                  promo_code: row['Promo Code'] || '',
                  promo_rate: row['Promo Rate'] || 0
                }
              };
            }

            return acc;
          }, {});

          // Convert grouped data to array
          this.importedData = Object.values(groupedData);
        };
        reader.readAsBinaryString(file);
      }
    },
    formatDate(date) {
      // Ensure date is in YYYY-MM-DD format
      if (!date) return new Date().toISOString().split('T')[0];
      return new Date(date).toISOString().split('T')[0];
    },
    generateOrderId() {
      // Generate a unique order ID
      return Math.random().toString(36).substring(2, 17).toUpperCase();
    },
    closeModal() {
      this.showModal = false;
      setTimeout(() => {
        this.$emit('closeModal');
      }, 200);
    },
    async postImportData() {
      // Reset progress
      this.importProgress = {
        current: 0,
        total: this.importedData.length,
        status: 'processing',
        currentItem: null,
        errors: [], // Reset errors array
        success: [],
        failedItemsFile: null,
      };

      try {
        // Process items sequentially
        for (let i = 0; i < this.importedData.length; i++) {
          const item = this.importedData[i];

          // Update current processing item
          this.importProgress.current = i + 1;
          this.importProgress.currentItem = item;

          try {
            // Attempt to process individual item
            await this.processImportItem(item);
            this.importProgress.success.push({
              item: item,
              details: {
                orderID: item.order_id,
                productName: item.items[0]?.product_name,
                totalAmount: item.grand_total
              }
            });
          } catch (itemError) {
            // Capture individual item errors
            console.log('item error', itemError);
            this.importProgress.errors.push(itemError);
          }

          // Optional: Add a small delay to show progress
          await new Promise(resolve => setTimeout(resolve, 500));
        }

        // Determine final status based on errors
        if (this.importProgress.errors.length > 0) {
          // Partial or full failure
          this.importProgress.status = 'error';
          this.$toast.add({
            severity: 'warn',
            summary: 'Import Sebagian',
            detail: `Berhasil mengimpor ${this.importedData.length - this.importProgress.errors.length} dari ${this.importedData.length} item`,
            life: 5000
          });
        } else {
          // Complete success
          this.importProgress.status = 'completed';
          this.$toast.add({
            severity: 'success',
            summary: 'Import Berhasil',
            detail: `Berhasil mengimpor ${this.importedData.length} item`,
            life: 3000
          });
        }
      } catch (error) {
        // Unexpected global error
        this.importProgress.status = 'error';
        this.$toast.add({
          severity: 'error',
          summary: 'Import Gagal',
          detail: error.message || 'Terjadi kesalahan fatal saat mengimpor data',
          life: 3000
        });
      }
    },
    async processImportItem(item) {
      // Replace with actual API call to save the item
      return new Promise((resolve, reject) => {
        // Simulated API call with detailed error checking
        setTimeout(async () => {
          try {
            // You would typically use an axios or fetch call here
            console.log('Processing item:', item);
            const response = await this.importSalesOrder(item);
            resolve(response);
          } catch (error) {
            // Reject with detailed error information
            reject({
              item: item,
              errorMessage: error.message,
              details: {
                orderID: item.order_id,
                productName: item.items[0]?.product_name,
                totalAmount: item.grand_total
              }
            });
          }
        }, 500);
      });
    },
    validateImportItem(item) {
      // Perform comprehensive validation
      if (!item.order_id) {
        throw new Error('Order ID tidak boleh kosong');
      }

      if (!item.items || item.items.length === 0) {
        throw new Error('Item penjualan tidak boleh kosong');
      }

      if (item.grand_total <= 0) {
        throw new Error('Total penjualan harus lebih dari 0');
      }

      // Add more validation rules as needed
      return true;
    },
    exportFailedItems() {
      // Convert failed items to Excel
      if (this.importProgress.errors.length === 0) return;

      // Group items by Order ID and expand each item
      const failedItemsData = [];

      this.importProgress.errors.forEach(error => {
        const item = error.item;

        // If the item has multiple items (products), create a row for each
        if (item.items && item.items.length > 0) {
          item.items.forEach((productItem) => {
            failedItemsData.push({
              'Commission Name': item.note_order || '',
              'Product ID': productItem.product_id || '',
              'Product Name': productItem.product_name || '',
              'Invoice No': item.order_id,
              'Total Product Amount': productItem.unit_price || 0,
              'Promo': item.discount || 0,
              'Promo Rate': item.discount_rate || 0,
              'Promo Code': item.additional_info?.promo_code || '',
              'Finish Date': item.delivery_date || '',
              'Service Fee Rate': item.additional_info?.service_fee_rate || 0,
              'Service Fee Gross': productItem.service_fee_gross || 0,
              'Service Fee Net': productItem.service_fee_net || 0,
              'PPN': productItem.ppn || 0,
              'PPH': productItem.pph || 0,
              'Error Message': error.errorMessage || 'Unknown error',
            });
          });
        }
      });

      // Ensure XLSX is available
      if (typeof XLSX === 'undefined') {
        this.$toast.add({
          severity: 'error',
          summary: 'Export Gagal',
          detail: 'Library XLSX tidak tersedia',
          life: 3000
        });
        return;
      }

      // Create worksheet
      const worksheet = XLSX.utils.json_to_sheet(failedItemsData);

      // Create workbook
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Failed Items');

      // Generate file
      const fileName = `failed_import_items_${new Date().toISOString().replace(/:/g, '-')}.xlsx`;
      XLSX.writeFile(workbook, fileName);

      // Optional: Create a download link
      this.importProgress.failedItemsFile = fileName;

      // Show toast notification
      this.$toast.add({
        severity: 'info',
        summary: 'Export Berhasil',
        detail: `Berhasil mengekspor ${this.importProgress.errors.length} item gagal`,
        life: 3000
      });
    },
    submitImportData() {
      this.$confirm.require({
        group: 'headless',
        header: 'Apakah anda yakin ingin menyimpan import data?',
        message: 'Silahkan konfirmasi sebelum diproses.',
        acceptLabel: 'Ya',
        rejectLabel: 'Tidak',
        accept: () => {
          if (this.importedData && this.importedData.length > 0) {
            this.postImportData();
            console.log("this.importProgress", this.importProgress);
          } else {
            this.$toast.add({
              severity: 'error',
              summary: 'Error',
              detail: 'Silakan unggah file Excel terlebih dahulu',
              life: 3000
            });
          }
        },
        reject: () => {
          this.$toast.add({
            severity: 'error',
            summary: 'Rejected',
            detail: 'Kamu telah membatalkan aksi simpan import data',
            life: 3000
          });
        }
      });
    },
  }
}
</script>
